import React, { useState, useEffect, useMemo } from "react";
import {
	Button,
	CircularProgress,
	TextField,
	Box,
	Typography,
	TableBody,
	TableCell,
	TableRow,
	TableHead,
	TableContainer,
	Paper,
	Table,
	Chip,
	FormControlLabel,
	RadioGroup,
	Radio,
} from "@mui/material";
import {
	fetchSavedLOA,
	getCategoryBasedOnInsuranceComapny,
	fetchAssignmentsById,
	saveLossAssessment,
} from "./utility/api";
import { useDispatch } from "react-redux";
import { setAssignment } from "./features/AssignmentSlice";
import { useParams } from "react-router-dom";
import { isSumInsuredAdequate } from "./utility/utils";
import {
	roundMoney,
	validateLossAssessment,
} from "./utility/claim-workflow";

const formatRM = (value) =>
	Number(value || 0).toLocaleString("en-MY", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

const TabulationForm = () => {
	const { assignmentName } = useParams();
	const dispatch = useDispatch();
	const [selectedAssignment, setselectedAssignment] = useState(null);
	const [formData, setFormData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [sumInsured, setSumInsured] = useState(0);
	const [totalArea, setTotalArea] = useState(0);
	const [perSqFtRate, setPerSqFtRate] = useState(0);
	const [excess, setExcess] = useState(0);
	// Derived, not state: both follow directly from the three inputs above.  They used to
	// be state updated from four separate call sites, three of which read stale values.
	const valueAtRisk = totalArea * perSqFtRate;
	const isAdequate = isSumInsuredAdequate(sumInsured, valueAtRisk);
	const sumInsuredType = isAdequate ? "adequate" : "inadequate";
	const [amountClaimSubmitted, setAmountClaimSubmitted] = useState("No");
	const [amountClaimed, setAmountClaimed] = useState("");
	const getDefaultSummaryRows = () => ([
		{ id: 1, description: "On building (excluding foundation)" },
		{ id: 2, description: "Less: Excess" },
		{ id: 3, description: "Final Adjustment" },
	]);
	const [summaryRows, setSummaryRows] = useState(getDefaultSummaryRows());


	// Move useMemo to the top level, before any returns
	const categoryRowSpans = useMemo(() => {
		return formData.reduce((spans, row) => {
			spans[row.categoryName] = (spans[row.categoryName] || 0) + 1;
			return spans;
		}, {});
	}, [formData]);

	const categoryFirstIndex = useMemo(() => {
		const firstIndex = {};
		formData.forEach((row, idx) => {
			if (!(row.categoryName in firstIndex)) {
				firstIndex[row.categoryName] = idx;
			}
		});
		return firstIndex;
	}, [formData]);

	const handleSumInsuredChange = (e) => {
		e.preventDefault();
		setSumInsured(Number(e.target.value) || 0);
	};

	const handleTotalAreaChange = (e) => {
		e.preventDefault();
		setTotalArea(Number(e.target.value) || 0);
	};

	const handlePerSqFtRateChange = (e) => {
		e.preventDefault();
		setPerSqFtRate(Number(e.target.value) || 0);
	};

	useEffect(() => {
		const fetchData = async () => {
			if (!assignmentName) {
				setError("Assignment name is required");
				return;
			}
			try {
				setIsLoading(true);
				setError(null);

				const assignmentResponse = await fetchAssignmentsById(
					assignmentName
				);
				if (!assignmentResponse || !assignmentResponse.assignmentName) {
					throw new Error("Invalid assignment data");
				}

				setselectedAssignment(assignmentResponse);
				const [categories, savedCategories] = await Promise.all([
					getCategoryBasedOnInsuranceComapny(
						assignmentResponse.insuranceCompany || ""
					),
					fetchSavedLOA(assignmentResponse.assignmentName),
				]);
				const rawData =
					savedCategories.length > 0 ? savedCategories : categories;

				setSumInsured(assignmentResponse.sumInsured || 0);
				setTotalArea(assignmentResponse.totalArea || 0);
				setPerSqFtRate(assignmentResponse.perSqftValue || 0);
				setExcess(assignmentResponse.excessValue || 0);
				setAmountClaimSubmitted(
					assignmentResponse.amountClaimSubmitted === "Yes"
						? "Yes"
						: "No"
				);
				setAmountClaimed(
					assignmentResponse.amountClaimed != null
						? assignmentResponse.amountClaimed
						: ""
				);
				const savedSummaryRows = Array.isArray(
					assignmentResponse.summaryDescriptions
				)
					? assignmentResponse.summaryDescriptions
					: [];
				setSummaryRows(
					getDefaultSummaryRows().map((defaultRow) => {
						const savedRow = savedSummaryRows.find(
							(row) => Number(row.id) === defaultRow.id
						);
						return savedRow && typeof savedRow.description === "string"
							? { ...defaultRow, description: savedRow.description }
							: defaultRow;
					})
				);

				// Define custom category order based on your table
				const categoryOrder = [
					"General Cleaning",
					"Painting",
					"Timber Flooring",
					"Doors(per unit)",
					"Electrical wiring(per unit)",
					"Electrical/AutoGate",
					"Flooring polishing",
					"Kitchen cabinet(built-in only)",
					"Others",
				];

				// Sort by custom category order
				const sortedData = [...rawData].sort((a, b) => {
					const aIndex = categoryOrder.indexOf(a.categoryName);
					const bIndex = categoryOrder.indexOf(b.categoryName);
					// If category not in order list, push to end
					if (aIndex === -1) return 1;
					if (bIndex === -1) return -1;
					return aIndex - bIndex;
				});
				

				const updatedData = sortedData.map((row) => {
					const rate = row.nRate && row.nRate !== 0 ? row.nRate : row.rate || 0;
					const assessment = rate * row.qty;
					const adjustmentOnIndemnity = row.dep
						? assessment * (1 - row.dep / 100)
						: assessment;
					return {
						...row,
						assessment,
						adjustmentOnIndemnity,
					};
				});
	
				setFormData(updatedData);
			} catch (err) {
				setError(err.message || "Failed to fetch data");
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	},[assignmentName]);

	const updateFormData = (index, field, value) => {
		setFormData((prevData) => {
			const updatedData = [...prevData];
			updatedData[index][field] = Number(value) || 0;
			// Use rate as the default if nRate is 0
			const rate = updatedData[index].nRate && updatedData[index].nRate !== 0
            ? updatedData[index].nRate
            : updatedData[index].rate;
			updatedData[index].assessment = rate * updatedData[index].qty;
			updatedData[index].adjustmentOnIndemnity = updatedData[index].dep
				? updatedData[index].assessment * (1 - updatedData[index].dep / 100)
				: updatedData[index].assessment;
			return updatedData;
		});
	};
	

	if (error) {
		return (
			<Box
				sx={{
					backgroundColor: "white",
					padding: "20px",
					borderRadius: "8px",
				}}
			>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					minHeight="200px"
				>
					<Typography color="error">{error}</Typography>
				</Box>
			</Box>
		);
	}

	if (isLoading || !formData.length) {
		return (
			<Box
				sx={{
					backgroundColor: "white",
					padding: "20px",
					borderRadius: "8px",
				}}
			>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					minHeight="200px"
				>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	if (!selectedAssignment || !assignmentName) {
		return (
			<Box
				sx={{
					backgroundColor: "white",
					padding: "20px",
					borderRadius: "8px",
				}}
			>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					minHeight="200px"
				>
					<Typography color="error">
						Missing required data to display the form
					</Typography>
				</Box>
			</Box>
		);
	}


	const handleSummaryDescChange = (rowId, value) => {
		setSummaryRows((prev) =>
			prev.map((r) => (r.id === rowId ? { ...r, description: value } : r))
		);
	};

	const totalAssessment = formData.reduce(
		(total, row) => total + Number(row.assessment || 0),
		0
	);
	const totalAssessmentAfterDep = formData.reduce(
		(total, row) => total + Number(row.adjustmentOnIndemnity || 0),
		0
	);
	const inAdequateTotal = valueAtRisk
		? (sumInsured / valueAtRisk) * totalAssessmentAfterDep
		: 0;
	// Average applies only when underinsured; keyed off the numeric rule rather than the
	// display label, so the saved settlement cannot inherit a wording bug.
	const adjustedLoss = isAdequate ? totalAssessmentAfterDep : inAdequateTotal;

	const handleItemChange = (index, value) => {
		const updatedData = [...formData];
		updatedData[index].customSubCategory = value;
		setFormData(updatedData);
	};

	const handleQtyChange = (index, value) =>
		updateFormData(index, "qty", value);
	const handleNRateChange = (index, value) =>
		updateFormData(index, "nRate", value);
	const handleDepChange = (index, value) =>
		updateFormData(index, "dep", value);

	const handleExcessChange = (value) => {
		setExcess(Number(value) || 0);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (isSubmitting) return; // Prevent multiple submissions

		const validationError = validateLossAssessment({
			sumInsured,
			totalArea,
			perSqFtRate,
			excess,
			totalAssessmentAfterDep,
			adjustedLoss,
		});
		if (validationError) {
			alert(validationError);
			return;
		}
		
		try {
			setIsSubmitting(true);
			const settledAmount = roundMoney(adjustedLoss - excess);
			const updatedAssignment = {
				...selectedAssignment,
				totalArea,
				perSqftValue: perSqFtRate,
				excessValue: excess,
				settledAmount,
				sumInsured,
				amountClaimSubmitted,
				amountClaimed:
					amountClaimSubmitted === "Yes"
						? Number(amountClaimed) || 0
						: null,
				summaryDescriptions: summaryRows,
				losstemplate: formData.map(
					({
						categoryName,
						subcategory,
						rate,
						nRate,
						qty,
						dep,
						customSubCategory,
					}) => ({
						category: categoryName || "",
						subCategory: categoryName === "Others" ? (customSubCategory || subcategory || "") : (subcategory || ""),
						rate: rate || 0,
						customRate: nRate || 0,
						Quantity: qty || 0,
						Depreciation: dep || 0,
					})
				),
			};

			dispatch(setAssignment(updatedAssignment));

			const losstemplate = formData.map((data) => ({
				category: data.categoryName || "",
				subCategory: data.categoryName === "Others" ? (data.customSubCategory || data.subcategory || "") : (data.subcategory || ""),
				rate: data.rate || 0,
				customRate: data.nRate || 0,
				Quantity: data.qty || 0,
				Depreciation: data.dep || 0,
			}));

			const payload = {
				settledAmount: updatedAssignment.settledAmount,
				sumInsured: updatedAssignment.sumInsured,
				totalArea: updatedAssignment.totalArea,
				perSqFtValue: updatedAssignment.perSqftValue,
				excessValue: updatedAssignment.excessValue,
				amountClaimSubmitted,
				amountClaimed:
					amountClaimSubmitted === "Yes"
						? Number(amountClaimed) || 0
						: "",
				assignmentName: selectedAssignment.assignmentName,
				losstemplate: JSON.stringify(losstemplate),
				// FormData does not serialize arrays of objects. Send JSON so the API can
				// persist the editable descriptions and reuse them in the final report.
				summaryDescriptions: JSON.stringify(summaryRows),
			};

			if (window.opener) {
				window.opener.postMessage(
					{ message: payload },
					window.location.origin
				);
				window.close();
			} else {
				// Fallback: Save LOA directly without snapshots/opener
				await saveLossAssessment(
					payload,
					selectedAssignment.insuranceCompany || ""
				);
				alert("Loss assessment saved.");
			}
		} catch (error) {
			console.error("Failed to submit assignment:", error);
			alert(
				`Failed to update assignment: ${
					error.message || "Unknown error"
				}`
			);
		} finally {
			// setIsSubmitting(false);
		}
	};

	return (
		<Box
			sx={{
				backgroundColor: "white",
				padding: "20px",
				borderRadius: "8px",
				boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
				maxWidth: "1300px",
				margin: "0 auto",
				marginTop: "10px",
			}}
		>
			<form onSubmit={handleSubmit}>
				<table
					style={{
						width: "100%",
						borderCollapse: "collapse",
						border: "1px solid black",
					}}
				>
					<thead>
						<tr>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								No
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Category
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Item
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Rate (RM)
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Negotiated Rate (RM)
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Qty
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Adjustment on RVC (RM)
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Dep (%)
							</th>
							<th
								style={{
									border: "1px solid black",
									padding: "8px",
									textAlign: "center",
								}}
							>
								Adjustment on Indemnity (RM)
							</th>
						</tr>
					</thead>
					<tbody>
						{formData.map((row, index) => (
							<tr key={row.id || index}>
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
										textAlign: "center",
									}}
								>
									{index + 1}
								</td>
								{categoryFirstIndex[row.categoryName] ===
								index ? (
									<td
										style={{
											border: "1px solid black",
											padding: "8px",
											textAlign: "center",
										}}
										rowSpan={
											categoryRowSpans[row.categoryName]
										}
									>
										{row.categoryName}
									</td>
								) : null}
								{/* Rest of the row remains unchanged */}
								{row.categoryName === "Others" ? (
									<td
										style={{
											border: "1px solid black",
											padding: "8px",
										}}
									>
										<input
											type="text"
											value={row.customSubCategory ?? ''}
											onChange={(e) =>
												handleItemChange(
													index,
													e.target.value
												)
											}
											style={{
												width: "100%",
												padding: "4px",
												boxSizing: "border-box",
											}}
											aria-label="Custom Subcategory"
										/>
									</td>
								) : (
									<td
										style={{
											border: "1px solid black",
											padding: "8px",
											textAlign: "left",
										}}
									>
										{row.subcategory}
									</td>
								)}
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
										textAlign: "right",
									}}
								>
									{row.rate}
								</td>
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
									}}
								>
									<input
										type="number"
										value={(row.nRate && row.nRate !== 0) ? row.nRate : (row.rate ?? 0)}
										onChange={(e) =>
											handleNRateChange(
												index,
												e.target.value
											)
										}
										style={{
											width: "100%",
											padding: "4px",
											textAlign: "right",
											boxSizing: "border-box",
										}}
										aria-label="Negotiated Rate"
									/>
								</td>
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
									}}
								>
									<input
										type="number"
										value={row.qty ?? 0}
										onChange={(e) =>
											handleQtyChange(
												index,
												e.target.value
											)
										}
										style={{
											width: "100%",
											padding: "4px",
											textAlign: "right",
											boxSizing: "border-box",
										}}
										aria-label="Quantity"
									/>
								</td>
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
										textAlign: "right",
									}}
								>
									{row.assessment}
								</td>
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
									}}
								>
									<input
										type="number"
										value={row.dep ?? 0}
										onChange={(e) =>
											handleDepChange(
												index,
												e.target.value
											)
										}
										style={{
											width: "100%",
											padding: "4px",
											textAlign: "right",
											boxSizing: "border-box",
										}}
										aria-label="Depreciation"
									/>
								</td>
								<td
									style={{
										border: "1px solid black",
										padding: "8px",
										textAlign: "right",
									}}
								>
									{row.adjustmentOnIndemnity}
								</td>
							</tr>
						))}
						<tr>
							<td
								colSpan="5"
								style={{
									fontWeight: "bold",
									border: "1px solid black",
									textAlign: "right",
									padding: "8px",
								}}
							>
								Total
							</td>
							<td style={{ border: "1px solid black" }}></td>
							<td
								style={{
									fontWeight: "bold",
									border: "1px solid black",
									textAlign: "right",
									padding: "8px",
								}}
							>
								{totalAssessment}
							</td>
							<td style={{ border: "1px solid black" }}></td>
							<td
								style={{
									fontWeight: "bold",
									border: "1px solid black",
									textAlign: "right",
									padding: "8px",
								}}
							>
								{totalAssessmentAfterDep}
							</td>
						</tr>
					</tbody>
				</table>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 3,
						mt: 3,
					}}
				>
					<Paper variant="outlined" sx={{ p: 2.5 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								flexWrap: "wrap",
								gap: 1,
								mb: 2,
							}}
						>
							<Typography
								variant="subtitle1"
								sx={{ fontWeight: 600 }}
							>
								Sum Insured &amp; Value at Risk
							</Typography>
							<Chip
								label={`Sum insured is ${sumInsuredType}`}
								color={
									sumInsuredType === "adequate"
										? "success"
										: "warning"
								}
								size="small"
							/>
						</Box>
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 2,
								mb: 2,
							}}
						>
							<TextField
								label="Sum Insured (RM)"
								type="number"
								value={sumInsured}
								onChange={(e) => handleSumInsuredChange(e)}
								required
								size="small"
								sx={{ width: { xs: "100%", sm: 220 } }}
							/>
							<TextField
								label="Total Area (sqft)"
								type="number"
								name="totalArea"
								value={totalArea}
								onChange={(e) => handleTotalAreaChange(e)}
								required
								size="small"
								sx={{ width: { xs: "100%", sm: 180 } }}
							/>
							<TextField
								label="Rate per sqft (RM)"
								type="number"
								name="perSqFtRate"
								value={perSqFtRate}
								onChange={(e) => handlePerSqFtRateChange(e)}
								required
								size="small"
								sx={{ width: { xs: "100%", sm: 180 } }}
							/>
						</Box>
						<Typography variant="body2" color="text.secondary">
							Value at Risk = {totalArea || 0} sqft × RM{" "}
							{formatRM(perSqFtRate)} per sqft ={" "}
							<Box
								component="span"
								sx={{ fontWeight: 600, color: "text.primary" }}
							>
								RM {formatRM(valueAtRisk)}
							</Box>
						</Typography>
						{sumInsuredType === "inadequate" && (
							<Box
								sx={{
									mt: 2,
									p: 1.5,
									bgcolor: "grey.50",
									border: "1px solid",
									borderColor: "grey.200",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="body2"
									color="text.secondary"
									gutterBottom
								>
									Average applies — the loss is prorated:
									Adjusted Loss = (Sum Insured ÷ Value at
									Risk) × Loss
								</Typography>
								<Typography variant="body2">
									(RM {formatRM(sumInsured)} ÷ RM{" "}
									{formatRM(valueAtRisk)}) × RM{" "}
									{formatRM(totalAssessmentAfterDep)} ={" "}
									<Box
										component="span"
										sx={{ fontWeight: 600 }}
									>
										RM {formatRM(inAdequateTotal)}
									</Box>
								</Typography>
							</Box>
						)}
					</Paper>

					<Paper variant="outlined" sx={{ p: 2.5 }}>
						<Typography
							variant="subtitle1"
							sx={{ fontWeight: 600, mb: 1 }}
						>
							Amount claim by the insured (If any)
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 2,
							}}
						>
							<RadioGroup
								row
								name="amountClaimSubmitted"
								aria-label="Amount claim by the insured"
								value={amountClaimSubmitted}
								onChange={(e) => {
									setAmountClaimSubmitted(e.target.value);
									if (e.target.value !== "Yes") {
										setAmountClaimed("");
									}
								}}
							>
								<FormControlLabel
									value="Yes"
									control={<Radio />}
									label="Yes"
								/>
								<FormControlLabel
									value="No"
									control={<Radio />}
									label="No"
								/>
							</RadioGroup>
							{amountClaimSubmitted === "Yes" ? (
								<TextField
									label="Amount claimed (RM)"
									type="number"
									value={amountClaimed}
									onChange={(e) =>
										setAmountClaimed(e.target.value)
									}
									required
									size="small"
									sx={{ width: { xs: "100%", sm: 250 } }}
								/>
							) : (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ fontStyle: "italic" }}
								>
									Will appear in the report as “Not
									officially submitted”
								</Typography>
							)}
						</Box>
					</Paper>

					<Paper variant="outlined" sx={{ p: 2.5 }}>
						<Typography
							variant="subtitle1"
							sx={{ fontWeight: 600, mb: 1 }}
						>
							Policy Excess
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 2,
							}}
						>
							<TextField
								label="Flood excess (RM)"
								type="number"
								name="excess"
								value={excess}
								onChange={(e) =>
									handleExcessChange(e.target.value)
								}
								required
								size="small"
								sx={{ width: { xs: "100%", sm: 220 } }}
							/>
							<Typography
								variant="body2"
								color="text.secondary"
							>
								The flood excess under the policy is deducted
								in the loss summary below.
							</Typography>
						</Box>
					</Paper>

					<Paper variant="outlined" sx={{ p: 2.5 }}>
						<Typography
							variant="subtitle1"
							sx={{ fontWeight: 600, mb: 1.5 }}
						>
							Loss Summary
						</Typography>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow
										sx={{ bgcolor: "grey.100" }}
									>
										<TableCell sx={{ width: 60 }}>
											<strong>Item</strong>
										</TableCell>
										<TableCell>
											<strong>Description</strong>
										</TableCell>
										<TableCell
											align="right"
											sx={{ width: 180 }}
										>
											<strong>Sum Insured (RM)</strong>
										</TableCell>
										<TableCell
											align="right"
											sx={{ width: 180 }}
										>
											<strong>Adjusted Loss (RM)</strong>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{summaryRows.map((row) => (
										<TableRow
											key={row.id}
											sx={
												row.id === 3
													? {
															bgcolor:
																"grey.50",
															"& td": {
																fontWeight:
																	"bold",
																borderTop:
																	"2px solid",
																borderTopColor:
																	"grey.400",
															},
													  }
													: undefined
											}
										>
											<TableCell>
												{row.id === 3 ? "" : row.id}
											</TableCell>
											<TableCell>
												<TextField
													variant="outlined"
													size="small"
													fullWidth
													value={row.description}
													onChange={(e) =>
														handleSummaryDescChange(
															row.id,
															e.target.value
														)
													}
													slotProps={{
														input: {
															sx: {
																fontSize:
																	"0.875rem",
																fontWeight:
																	row.id === 3
																		? "bold"
																		: "normal",
																bgcolor:
																	"background.paper",
															},
														},
													}}
													aria-label={`Description row ${row.id}`}
												/>
											</TableCell>
											<TableCell align="right">
												{row.id === 1
													? formatRM(sumInsured)
													: row.id === 2
													? formatRM(0)
													: ""}
											</TableCell>
											<TableCell align="right">
												{formatRM(
													row.id === 1
														? sumInsuredType ===
														  "inadequate"
															? inAdequateTotal
															: totalAssessmentAfterDep
														: row.id === 2
														? excess
														: sumInsuredType ===
														  "inadequate"
														? inAdequateTotal -
														  excess
														: totalAssessmentAfterDep -
														  excess
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						mt: 3,
					}}
				>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						size="large"
						disabled={isSubmitting}
						startIcon={
							isSubmitting && <CircularProgress size={20} />
						}
					>
						{isSubmitting ? "Submitting..." : "Submit"}
					</Button>
				</Box>
			</form>
		</Box>
	);
};

export default TabulationForm;
